// MaturityPoliciesReport.js
import React, { useState, useEffect, useRef } from "react";
import { FiChevronDown } from "react-icons/fi";
import { Mail } from "lucide-react";
import { FaFilePdf, FaRegFilePdf } from "react-icons/fa";
import { RiFileExcel2Line } from "react-icons/ri";
import { Formik, Form, Field } from "formik";
import { useNavigate } from "react-router-dom";
import { useGetGroupsQuery, useLazyPreviewPolicyReportQuery, usePolicyReportList } from "../../../../../services/redux/api/components";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { API_BASE_URL } from '../../../../../services/config';
import ReportLoader from '../LifePolicyRegister/ReportLoader';

/* Tailwind tokens (ensure you have brandRed in tailwind.config.js) */
const fieldBase =
  "rounded-md border border-gray-200 px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-sky-200 ";
const labelBase = " text-gray-700";
const sectionHeader = "bg-gray-100 p-3 rounded-md font-semibold text-gray-600";
const btnPrimary =
  "bg-brandRed hover:bg-red-600 text-white px-6 py-2 rounded-md shadow-md transition";

function useOutsideClick(ref, handler) {
  React.useEffect(() => {
    const onClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) handler();
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [ref, handler]);
}

/**
 * Normalize mutation/query hook shapes for consistent usage
 */
function normalizeHook(hook) {
  let trigger;
  let result = {};

  if (Array.isArray(hook)) {
    trigger = hook[0];
    result = hook[1] ?? {};
  } else if (hook && typeof hook === 'object') {
    trigger = hook.trigger ?? hook.mutate ?? hook;
    result = {
      data: hook.data,
      isLoading: hook.isLoading,
      error: hook.error,
      isError: hook.isError,
      isSuccess: hook.isSuccess,
    };
  }

  return { trigger, result };
}

function MultiSelectInline({ options = [], value = [], onChange, placeholder = "Select groups..." }) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const rootRef = useRef(null);
  useOutsideClick(rootRef, () => setOpen(false));

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`${fieldBase} min-h-[40px] py-2 w-full flex items-center gap-2 justify-between bg-white`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 flex-1 flex-wrap max-h-24 overflow-y-auto">
          {value.length === 0 ? (
            <span className="text-gray-400 truncate">{placeholder}</span>
          ) : (
            <div className="flex items-center gap-1 flex-wrap">
              {value.map((id) => {
                const o = options.find((x) => x.id === id);
                return (
                  <span key={id} className="bg-gray-100 px-2 py-0.5 rounded-md text-gray-700 whitespace-nowrap text-sm">
                    {o?.label ?? id}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-gray-500 text-sm">{value.length > 0 ? `${value.length} Groups Selected` : ""}</span>
          <FiChevronDown className="text-gray-500" />
        </div>
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 shadow-lg rounded-md overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-52 overflow-auto p-2">
            {filteredOptions.length === 0 ? (
              <div className="text-gray-400 text-center py-2">No groups found</div>
            ) : (
              filteredOptions.map((o) => (
                <label key={o.id} className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer">
                  <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} className="w-4 h-4" />
                  <span className="">{o.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MaturityPoliciesReport() {
  const initialValues = {
    fromMaturityDate: "",
    toMaturityDate: "",
    includeAnnuityPolicies: false,
    includeRecordOnlyPolicies: false,
    sortingOrder: "groupwise", // groupwise | memberwise | areawise | maturitydatewise
    groupIds: [],
    includeAddress: false,
    includeMobile: false,
    includeEmail: false,
  };

  const navigate = useNavigate();
  const { data: groupsResult } = useGetGroupsQuery({ pageNumber: 1, pageSize: 1000 });

  // RTK Query hooks
  const previewHook = useLazyPreviewPolicyReportQuery();
  const { trigger: triggerPreview, result: previewResult } = normalizeHook(previewHook);

  const policyReportListHook = usePolicyReportList();
  const { trigger: triggerPolicyReportList } = normalizeHook(policyReportListHook);

  // State for modals and Excel export
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [excelPayload, setExcelPayload] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const groupsOptions =
    groupsResult?.data?.map((g) => ({
      id: String(g.GroupId ?? g.id ?? g.GroupID ?? ""),
      label: g.GroupName ?? g.GroupCode ?? String(g.GroupID ?? g.id ?? ""),
    })) || [];

  // Build payload helper
  const buildPayload = (values) => {
    return {
      fromMaturityDate: values.fromMaturityDate,
      toMaturityDate: values.toMaturityDate,
      includeAnnuityPolicies: values.includeAnnuityPolicies ? '1' : '0',
      includeRecordOnlyPolicies: values.includeRecordOnlyPolicies ? '1' : '0',
      sortBy: values.sortingOrder,
      sortingOrder: 'asc',
      groupIds: values.groupIds.length > 0 ? values.groupIds.join(',') : '',
      includeAddress: values.includeAddress ? '1' : '0',
      includeMobile: values.includeMobile ? '1' : '0',
      includeEmail: values.includeEmail ? '1' : '0',
      policyStatus: 'matured',
      filterByDate: 'maturity',
      includeLapsed: '0',
    };
  };

  // Handle PDF Preview
  const handlePdfPreview = async (values) => {
    const payload = buildPayload(values);
    try {
      setIsGenerating(true);
      const response = await triggerPreview(payload);
      if (response?.data?.pdfBase64) {
        const byteCharacters = atob(response.data.pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
        setPdfModalOpen(true);
      }
    } catch (error) {
      console.error('PDF preview error:', error);
      alert('Failed to generate PDF preview');
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle Excel Download
  const handleExcelDownload = (values) => {
    const payload = buildPayload(values);
    setExcelPayload(payload);
  };

  // Excel generation effect
  useEffect(() => {
    if (!excelPayload) return;

    const generateExcel = async () => {
      try {
        const response = await triggerPolicyReportList(excelPayload);
        const reportData = response?.data?.data || [];

        if (reportData.length === 0) {
          alert('No data available for the selected criteria');
          setExcelPayload(null);
          return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Maturity Policies Report');

        // Define columns
        worksheet.columns = [
          { header: 'Policy No', key: 'PolicyNo', width: 15 },
          { header: 'Customer Name', key: 'CustomerName', width: 25 },
          { header: 'Group', key: 'Group', width: 20 },
          { header: 'Area', key: 'Area', width: 20 },
          { header: 'Plan', key: 'Plan', width: 20 },
          { header: 'Sum Assured', key: 'SumAssured', width: 15 },
          { header: 'Premium', key: 'Premium', width: 15 },
          { header: 'Maturity Date', key: 'MaturityDate', width: 15 },
          { header: 'Status', key: 'Status', width: 15 },
        ];

        // Add rows
        reportData.forEach(row => {
          worksheet.addRow({
            PolicyNo: row.PolicyNo || '',
            CustomerName: row.CustomerName || '',
            Group: row.GroupName || '',
            Area: (row.raw && row.raw.AreaName) || row.AreaName || row.Area || '',
            Plan: row.PlanName || '',
            SumAssured: row.SumAssured || '',
            Premium: row.Premium || '',
            MaturityDate: row.MaturityDate || '',
            Status: row.Status || '',
          });
        });

        // Style header
        worksheet.getRow(1).font = { bold: true };
        worksheet.getRow(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' }
        };

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Maturity_Policies_Report_${new Date().toISOString().split('T')[0]}.xlsx`);

        setExcelPayload(null);
      } catch (error) {
        console.error('Excel generation error:', error);
        alert('Failed to generate Excel file');
        setExcelPayload(null);
      }
    };

    generateExcel();
  }, [excelPayload, triggerPolicyReportList]);

  const handleGenerate = (values) => {
    const payload = buildPayload(values);
    navigate("/maturity-policies-report-preview", { state: { payload } });
  };

  // Close PDF modal
  const closePdfModal = () => {
    setPdfModalOpen(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  return (
    <div className="w-full p-4">
      <div style={{ boxShadow: "0 0 8px #ccc" }} className="w-full rounded bg-white">
        <Formik initialValues={initialValues} onSubmit={handleGenerate}>
          {({ values, setFieldValue }) => (
            <Form className="w-full">
              <div className="p-2 border-b border-brandRed">
                <div className="flex justify-between items-center">
                  <h5 className="text-brandRed font-bold m-3">Maturity Policy Report</h5>
                  <div className="flex gap-3 items-center">
                    <Mail size={38} className="text-brandRed cursor-pointer" />
                    <RiFileExcel2Line 
                      size={38} 
                      className="text-green-700 cursor-pointer hover:text-green-800" 
                      onClick={() => handleExcelDownload(values)}
                      title="Download Excel"
                    />
                    <FaFilePdf 
                      size={37} 
                      className="text-red-600 cursor-pointer hover:text-red-700" 
                      onClick={() => handlePdfPreview(values)}
                      title="Preview PDF"
                    />
                  </div>
                </div>
              </div>
              <div className="p-6">
                {/* Filter Selection -> Maturity Period + checkboxes */}
                <h6 className="text-gray-700 font-bold my-3 bg-gray-100 rounded p-2">Filter Selection</h6>

                <div className="grid grid-cols-12 gap-3 items-center p-2">
                  <div className="col-span-12 lg:col-span-2">
                    <div className={`${labelBase} mb-0.5`}>Maturity Period</div>
                  </div>

                  <div className="col-span-12 lg:col-span-8 flex flex-wrap gap-3 items-center">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-700">From</span>
                      <Field type="date" name="fromMaturityDate" className={`${fieldBase} h-10 w-48`} />
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-gray-700">To</span>
                      <Field type="date" name="toMaturityDate" className={`${fieldBase} h-10 w-48`} />
                    </div>

                    <div className="flex items-center gap-6 mt-3">
                      <label className="flex items-center gap-2">
                        <Field type="checkbox" name="includeAnnuityPolicies" className="w-4 h-4" />
                        <span className="ml-1 text-gray-700">Include Annuity Policies</span>
                      </label>

                      <label className="flex items-center gap-2">
                        <Field type="checkbox" name="includeRecordOnlyPolicies" className="w-4 h-4" />
                        <span className="ml-1 text-gray-700">Include Record Only Policies</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Sorting Order */}
                <h6 className="text-gray-700 font-bold my-3 bg-gray-100 rounded p-2">Sorting Order</h6>
                <div className="flex flex-wrap items-center gap-6 p-3">
                  <label className="flex items-center gap-2">
                    <Field type="radio" name="sortingOrder" value="groupwise" className="w-4 h-4" />
                    <span className="ml-1 text-gray-700 font-medium">Group wise</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <Field type="radio" name="sortingOrder" value="memberwise" className="w-4 h-4" />
                    <span className="ml-1 text-gray-700 font-medium">Member wise</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <Field type="radio" name="sortingOrder" value="areawise" className="w-4 h-4" />
                    <span className="ml-1 text-gray-700 font-medium">Area wise</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <Field type="radio" name="sortingOrder" value="maturitydatewise" className="w-4 h-4" />
                    <span className="ml-1 text-gray-700 font-medium">Maturity Date wise</span>
                  </label>
                </div>

                {/* Select Groups */}
                <div className="grid grid-cols-12 gap-3 items-center p-2">
                  <div className="col-span-12 lg:col-span-2">
                    <div className={`${labelBase} mb-0.5`}>Select Groups</div>
                  </div>

                  <div className="col-span-12 lg:col-span-6">
                    <MultiSelectInline
                      options={groupsOptions}
                      value={values.groupIds}
                      onChange={(v) => setFieldValue("groupIds", v)}
                      placeholder="Select groups..."
                    />
                  </div>

                  <div className="col-span-12 lg:col-span-4 flex items-center lg:justify-start justify-end">
                    {values.groupIds.length > 0 && (
                      <div className="hidden lg:block text-blue-600 hover:underline ml-3">{values.groupIds.length} Groups Selected</div>
                    )}
                  </div>

                  {values.groupIds.length > 0 && (
                    <div className="col-span-12 lg:hidden text-center mt-2 text-blue-600 hover:underline">Groups Selected: {values.groupIds.length}</div>
                  )}
                </div>

                {/* Add in Report */}
                <h6 className="text-gray-700 font-bold mb-3 my-3 bg-gray-100 rounded p-2">Add in Report</h6>
                <div className="flex gap-6 items-center py-4 flex-wrap p-3">
                  <label className="flex items-center gap-2">
                    <Field type="checkbox" name="includeAddress" className="w-4 h-4" />
                    <span className="ml-1 text-gray-700">Address</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <Field type="checkbox" name="includeMobile" className="w-4 h-4" />
                    <span className="ml-1 text-gray-700">Mobile</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <Field type="checkbox" name="includeEmail" className="w-4 h-4" />
                    <span className="ml-1 text-gray-700">Email</span>
                  </label>
                </div>

                <div className="flex justify-center mt-8">
                  <button type="submit" className={btnPrimary}>
                    Generate 
                  </button>
                </div>

                <div className="mt-6 text-center text-gray-500">Maturity Policies Report..</div>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* PDF Preview Modal */}
      {pdfModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">Maturity Policies Report Preview</h3>
              <div className="flex gap-2">
                <a href={pdfBlobUrl} download="Maturity_Policies_Report.pdf" className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">Download</a>
                <button
                  onClick={closePdfModal}
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              {pdfBlobUrl && (
                <iframe
                  src={pdfBlobUrl}
                  className="w-full h-full"
                  title="PDF Preview"
                />
              )}
            </div>
          </div>
        </div>
      )}
      <ReportLoader visible={isGenerating} message="PDF report is generating..." />
    </div>
  );
}
